import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { CurrentUserResponse } from '@shared/api-types';
import { ApiError, apiFetch } from './apiClient';
import { AuthContext, type AuthState } from './auth';

interface Props {
  children: ReactNode;
  /**
   * Test seam — when provided, the provider skips the initial /api/me probe
   * and starts in this state. Used by the Login navigation test.
   */
  initialState?: AuthState;
}

export function AuthProvider({ children, initialState }: Props) {
  const [state, setState] = useState<AuthState>(initialState ?? { status: 'unknown' });

  const refresh = useCallback(async () => {
    try {
      const user = await apiFetch<CurrentUserResponse>('/api/me');
      setState({ status: 'authenticated', user });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setState({ status: 'anonymous' });
        return;
      }
      // Backend not yet exposing /api/me (Phase 5 follow-up): treat as
      // anonymous so the user can still hit /login. The Login flow itself
      // doesn't depend on /api/me.
      if (err instanceof ApiError && err.status === 404) {
        setState({ status: 'anonymous' });
        return;
      }
      setState({ status: 'anonymous' });
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiFetch<void>('/api/auth/logout', { method: 'POST' });
    } catch {
      // best-effort
    }
    setState({ status: 'anonymous' });
  }, []);

  useEffect(() => {
    if (initialState) return;
    // The /api/me probe is an external sync per React's effect guidance;
    // wrapping it in a microtask keeps the setState out of the render path.
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (!cancelled) void refresh();
    });
    return () => {
      cancelled = true;
    };
  }, [initialState, refresh]);

  const value = useMemo(() => ({ state, refresh, signOut }), [state, refresh, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
